import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useTranslation } from "react-i18next";
import { EmptyState, ErrorState, LoadingState } from "@/components/states";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AssistantMessage } from "@/components/AssistantMessage";
import { MediaAttachButton } from "@/components/MediaAttachButton";
import {
  ChevronLeft, Loader2, Play, Sparkles, Clock, BookOpen, Wrench, FolderOpen, Zap, Plus, Trash2,
} from "lucide-react";
import {
  generateTheory, generateTopicPlan, quickStudy, type TopicPlanDoc,
} from "@/lib/curriculum.functions";

export const Route = createFileRoute("/_app/programs/$curriculumId/$topicId")({ component: TopicPage });

/* -------- subject-aware tool suggestions -------- */
const TOOLS: Record<string, string[]> = {
  storia: ["Timeline", "Cause → conseguenze", "Flashcard date", "Quiz", "Mappa"],
  history: ["Timeline", "Causes → effects", "Date flashcards", "Quiz", "Map"],
  filosofia: ["Mappa concettuale", "Confronto tra filosofi", "Active Recall", "Simulazione interrogazione"],
  philosophy: ["Concept map", "Compare thinkers", "Active recall", "Mock oral exam"],
  matematica: ["Formulario", "Esercizi guidati", "Esercizi progressivi", "Error log", "Quiz formule"],
  maths: ["Formula sheet", "Guided exercises", "Progressive drills", "Error log", "Formula quiz"],
  fisica: ["Formulario", "Esercizi guidati", "Diagrammi", "Quiz"],
  inglese: ["Vocabulary", "Flashcard", "Listening", "Grammar", "Speaking AI", "Writing"],
  english: ["Vocabulary", "Flashcards", "Listening", "Grammar", "Speaking AI", "Writing"],
  spagnolo: ["Vocabulary", "Flashcard", "Listening", "Grammar", "Speaking AI"],
  scienze: ["Diagrammi", "Processi", "Mappe", "Flashcard", "Quiz"],
  biologia: ["Diagrammi", "Processi", "Mappe", "Flashcard", "Quiz"],
  arte: ["Timeline", "Analisi opera", "Mappa", "Flashcard"],
};
function toolsFor(subject: string): string[] {
  const key = Object.keys(TOOLS).find((k) => subject.toLowerCase().includes(k));
  return key ? TOOLS[key] : ["Mappa", "Flashcard", "Quiz", "Active Recall", "Ripasso"];
}

function knowledgeLabel(score: number, tKey: (k: string) => string) {
  if (score === 0) return tKey("programs.knowledge.to_start");
  if (score < 40) return tKey("programs.knowledge.learning");
  if (score < 70) return tKey("programs.knowledge.almost");
  if (score < 85) return tKey("programs.knowledge.ready");
  return tKey("programs.knowledge.ready");
}

function daysUntil(date: string) {
  const d = new Date(`${date}T00:00:00`).getTime();
  const today = new Date(new Date().toISOString().slice(0, 10) + "T00:00:00").getTime();
  return Math.round((d - today) / 86400000);
}

function TopicPage() {
  const { curriculumId, topicId } = Route.useParams();
  const { t } = useTranslation();
  const qc = useQueryClient();

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["topic", topicId],
    queryFn: async () => {
      const [{ data: topic }, { data: cur }, { data: materials }, { data: plans }, { data: theory }] =
        await Promise.all([
          supabase.from("curriculum_topics").select("*").eq("id", topicId).maybeSingle(),
          supabase.from("curricula").select("subject_name,emoji").eq("id", curriculumId).maybeSingle(),
          supabase.from("topic_materials").select("*").eq("topic_id", topicId).order("created_at", { ascending: false }),
          supabase.from("topic_study_plans").select("*").eq("topic_id", topicId).order("created_at", { ascending: false }).limit(1),
          supabase.from("topic_theory").select("*").eq("topic_id", topicId).order("created_at", { ascending: false }).limit(1),
        ]);
      return {
        topic, cur,
        materials: materials ?? [],
        plan: (plans?.[0] as { plan: TopicPlanDoc; exam_date: string | null } | undefined) ?? null,
        theory: (theory?.[0]?.body as string | undefined) ?? null,
      };
    },
  });

  if (isLoading) {
    return <div className="px-5 pt-10"><LoadingState /></div>;
  }
  if (isError) {
    return <div className="px-5 pt-10"><ErrorState error={error} onRetry={() => refetch()} /></div>;
  }
  if (!data?.topic) {
    return (
      <div className="px-5 pt-10">
        <EmptyState title={t("states.notFoundTitle")} description={t("states.notFoundBody")} />
      </div>
    );
  }

  const topic = data.topic;
  const subject = data.cur?.subject_name ?? "";
  const score = topic.knowledge_score ?? 0;

  return (
    <div className="space-y-5 px-5 pt-8 pb-8">
      <Link to="/programs/$curriculumId" params={{ curriculumId }} className="inline-flex items-center gap-1 text-xs text-muted-foreground">
        <ChevronLeft className="h-3.5 w-3.5" /> {subject}
      </Link>

      <header className="space-y-1">
        <div className="text-xs text-muted-foreground">
          {subject} → {topic.title}
        </div>
        <h1 className="text-2xl font-semibold tracking-tight">{topic.title}</h1>
      </header>

      <Card className="card-soft space-y-3 rounded-3xl border-0 p-5">
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium">{t("programs.topicPage.knowledge")}</span>
          <span className="font-semibold tabular-nums text-primary">{score}%</span>
        </div>
        <Progress value={score} className="h-2" />
        <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
          <span>{knowledgeLabel(score, t)}</span>
          <span className="inline-flex items-center gap-1">
            <Clock className="h-3 w-3" /> {Math.floor(topic.estimated_minutes / 60)}h {topic.estimated_minutes % 60}m
          </span>
          <span>{t("programs.topicPage.difficulty")}: {topic.difficulty}</span>
        </div>
      </Card>

      <Tabs defaultValue="study">
        <TabsList className="w-full">
          <TabsTrigger value="study" className="flex-1 text-xs">{t("programs.topicPage.study")}</TabsTrigger>
          <TabsTrigger value="theory" className="flex-1 text-xs">{t("programs.topicPage.theory")}</TabsTrigger>
          <TabsTrigger value="tools" className="flex-1 text-xs">{t("programs.topicPage.tools")}</TabsTrigger>
          <TabsTrigger value="materials" className="flex-1 text-xs">{t("programs.topicPage.materials")}</TabsTrigger>
        </TabsList>

        <TabsContent value="study" className="space-y-4 pt-4">
          <StudySection
            topicId={topicId}
            topicTitle={topic.title}
            subject={subject}
            plan={data.plan?.plan ?? null}
            examDate={data.plan?.exam_date ?? topic.exam_date ?? null}
            onChanged={() => qc.invalidateQueries({ queryKey: ["topic", topicId] })}
          />
          <QuickStudyCard topicId={topicId} examDate={data.plan?.exam_date ?? topic.exam_date ?? null} />
        </TabsContent>

        <TabsContent value="theory" className="space-y-4 pt-4">
          <TheorySection topicId={topicId} initial={data.theory} />
        </TabsContent>

        <TabsContent value="tools" className="space-y-3 pt-4">
          <Card className="card-soft space-y-3 rounded-3xl border-0 p-5">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <Wrench className="h-4 w-4 text-primary" /> {t("programs.toolsFor")}
            </div>
            <div className="flex flex-wrap gap-2">
              {toolsFor(subject).map((tool) => (
                <span key={tool} className="rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                  {tool}
                </span>
              ))}
            </div>
            <div className="grid grid-cols-3 gap-2 pt-1">
              <Link to="/quiz" className="rounded-2xl bg-muted/60 p-3 text-center text-xs font-medium">Quiz</Link>
              <Link to="/flashcards" className="rounded-2xl bg-muted/60 p-3 text-center text-xs font-medium">Flashcard</Link>
              <Link to="/tools" className="rounded-2xl bg-muted/60 p-3 text-center text-xs font-medium">Mappe</Link>
            </div>
            <Link to="/learn" className="inline-flex items-center gap-1 text-xs text-primary">
              <Sparkles className="h-3.5 w-3.5" /> Imparare ad imparare
            </Link>
          </Card>
        </TabsContent>

        <TabsContent value="materials" className="space-y-3 pt-4">
          <MaterialsSection
            topicId={topicId}
            materials={data.materials as { id: string; title: string; content: string | null }[]}
            onChanged={() => qc.invalidateQueries({ queryKey: ["topic", topicId] })}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ------------------------------- STUDY ---------------------------------- */

function StudySection({
  topicId, topicTitle, subject, plan, examDate, onChanged,
}: {
  topicId: string; topicTitle: string; subject: string;
  plan: TopicPlanDoc | null; examDate: string | null; onChanged: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const run = useServerFn(generateTopicPlan);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [date, setDate] = useState(examDate ?? "");
  const [minutes, setMinutes] = useState(30);
  const [sources, setSources] = useState<"app" | "mine" | "both">("app");
  const [style, setStyle] = useState<"ai" | "fast" | "deep" | "review" | "custom">("ai");
  const [notes, setNotes] = useState("");
  const [done, setDone] = useState<Record<string, boolean>>({});

  const today = new Date().toISOString().slice(0, 10);
  const left = examDate ? daysUntil(examDate) : null;

  async function create() {
    if (!date) return toast.error(t("programs.wizard.when"));
    setBusy(true);
    try {
      await run({ data: { topicId, examDate: date, minutesPerDay: minutes, sources, style, notes: notes || undefined, addToPlanner: true } });
      toast.success(t("programs.plan.inPlanner"));
      setOpen(false);
      onChanged();
    } catch (e) {
      const msg = (e as Error).message;
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  }

  function startSession(minutesForBlock: number, blockTitle: string, method: string) {
    try {
      localStorage.setItem(
        "lumen.session-intent",
        JSON.stringify({ subject, topic: topicTitle, goal: blockTitle, minutes: minutesForBlock, method }),
      );
    } catch { /* ignore */ }
    navigate({ to: "/studio" });
  }

  const todayDay = useMemo(() => plan?.days.find((d) => d.date === today), [plan, today]);

  return (
    <div className="space-y-4">
      {left !== null && left <= 1 && (
        <Card className="rounded-3xl border-0 bg-amber-500/10 p-4 text-center text-sm font-semibold text-amber-700">
          {t("programs.plan.tomorrow")}
        </Card>
      )}

      {!open && (
        <Button className="h-14 w-full rounded-3xl text-sm font-semibold" onClick={() => setOpen(true)}>
          <Play className="mr-2 h-4 w-4" /> {t("programs.wizard.cta")}
        </Button>
      )}

      {open && (
        <Card className="card-soft space-y-4 rounded-3xl border-0 p-5">
          <div>
            <div className="mb-1 text-sm font-medium">{t("programs.wizard.when")}</div>
            <Input type="date" value={date} min={today} onChange={(e) => setDate(e.target.value)} />
          </div>
          <div>
            <div className="mb-1 text-sm font-medium">{t("programs.wizard.howLong")}</div>
            <div className="flex flex-wrap gap-2">
              {[15, 30, 45, 60].map((m) => (
                <button key={m} type="button" onClick={() => setMinutes(m)}
                  className={`rounded-full border px-3 py-1.5 text-xs ${minutes === m ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground"}`}>
                  {m} min
                </button>
              ))}
              <Input type="number" min={10} max={240} value={minutes}
                onChange={(e) => setMinutes(Number(e.target.value) || 30)} className="h-8 w-20 text-xs" />
            </div>
          </div>
          <div>
            <div className="mb-1 text-sm font-medium">{t("programs.wizard.from")}</div>
            <div className="flex flex-wrap gap-2">
              {([["app", "sourceApp"], ["mine", "sourceMine"], ["both", "sourceBoth"]] as const).map(([v, k]) => (
                <button key={v} type="button" onClick={() => setSources(v)}
                  className={`rounded-full border px-3 py-1.5 text-xs ${sources === v ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground"}`}>
                  {t(`programs.wizard.${k}`)}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="mb-1 text-sm font-medium">{t("programs.wizard.how")}</div>
            <div className="flex flex-wrap gap-2">
              {([["ai", "styleAi"], ["fast", "styleFast"], ["deep", "styleDeep"], ["review", "styleReview"], ["custom", "styleCustom"]] as const).map(([v, k]) => (
                <button key={v} type="button" onClick={() => setStyle(v)}
                  className={`rounded-full border px-3 py-1.5 text-xs ${style === v ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground"}`}>
                  {t(`programs.wizard.${k}`)}
                </button>
              ))}
            </div>
          </div>
          {style === "custom" && (
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={t("programs.wizard.notes")} className="rounded-2xl" />
          )}
          <div className="flex gap-2">
            <Button className="flex-1 rounded-2xl" disabled={busy} onClick={create}>
              {busy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t("programs.wizard.creating")}</> : t("programs.wizard.create")}
            </Button>
            <Button variant="ghost" onClick={() => setOpen(false)}>✕</Button>
          </div>
        </Card>
      )}

      {plan && (
        <Card className="card-soft space-y-4 rounded-3xl border-0 p-5">
          <div>
            <div className="text-sm font-semibold">{plan.title}</div>
            {examDate && (
              <div className="text-xs text-muted-foreground">
                {new Date(`${examDate}T00:00:00`).toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" })}
                {left !== null && left >= 0 ? ` · ${t("programs.plan.daysLeft", { count: left })}` : ""}
              </div>
            )}
            <div className="mt-1 text-xs text-muted-foreground">
              {t("programs.plan.needed")}: {Math.floor(plan.totalMinutes / 60)}h {plan.totalMinutes % 60}m
            </div>
          </div>

          {plan.summary && <p className="text-xs leading-relaxed text-muted-foreground">{plan.summary}</p>}

          <div className="space-y-3">
            {plan.days.map((d) => {
              const isToday = d.date === today;
              return (
                <div key={d.date} className={`rounded-2xl p-3 ${isToday ? "bg-primary/10" : "bg-muted/40"}`}>
                  <div className="mb-2 flex items-center justify-between">
                    <div className="text-xs font-semibold uppercase tracking-wide">
                      {isToday ? `${t("programs.plan.today")} · ` : ""}
                      {new Date(`${d.date}T00:00:00`).toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "short" })}
                    </div>
                    <div className="text-xs font-medium text-muted-foreground">{d.minutes} min</div>
                  </div>
                  <div className="space-y-1.5">
                    {d.blocks.map((b, bi) => {
                      const key = `${d.date}-${bi}`;
                      return (
                        <div key={key} className="flex items-center gap-2 rounded-xl bg-background/70 px-3 py-2">
                          <button
                            type="button"
                            onClick={() => setDone((p) => ({ ...p, [key]: !p[key] }))}
                            className={`h-4 w-4 shrink-0 rounded-full border text-[9px] leading-none ${done[key] ? "border-primary bg-primary text-primary-foreground" : "border-muted-foreground/40"}`}
                            aria-label={t("programs.plan.done")}
                          >
                            {done[key] ? "✓" : ""}
                          </button>
                          <div className="min-w-0 flex-1">
                            <div className={`truncate text-xs font-medium ${done[key] ? "line-through opacity-60" : ""}`}>{b.title}</div>
                            <div className="text-xs text-muted-foreground">{b.minutes} min · {b.method}</div>
                          </div>
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs"
                            onClick={() => startSession(b.minutes, b.title, b.method)}>
                            {t("programs.plan.start")}
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {plan.tips?.length > 0 && (
            <ul className="list-disc space-y-1 pl-4 text-xs text-muted-foreground">
              {plan.tips.map((tip, i) => <li key={i}>{tip}</li>)}
            </ul>
          )}

          <Button variant="outline" size="sm" className="w-full rounded-2xl" disabled={busy}
            onClick={() => { setDate(examDate ?? date); setOpen(true); }}>
            {t("programs.plan.regenerate")}
          </Button>
        </Card>
      )}

      {plan && todayDay && (
        <p className="text-center text-xs text-muted-foreground">{t("programs.plan.inPlanner")}</p>
      )}
    </div>
  );
}

/* ---------------------------- QUICK STUDY -------------------------------- */

function QuickStudyCard({ topicId, examDate }: { topicId: string; examDate: string | null }) {
  const { t } = useTranslation();
  const run = useServerFn(quickStudy);
  const [minutes, setMinutes] = useState(10);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  async function go() {
    setBusy(true);
    setText("");
    try {
      const res = await run({
        data: { topicId, minutes, examTomorrow: examDate ? daysUntil(examDate) <= 1 : false },
      });
      setText(res.text);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="card-soft space-y-3 rounded-3xl border-0 p-5">
      <div className="flex items-center gap-2 text-sm font-semibold">
        <Zap className="h-4 w-4 text-primary" /> {t("programs.quick.title")}
      </div>
      <div className="flex flex-wrap gap-2">
        {[5, 10, 20, 30].map((m) => (
          <button key={m} type="button" onClick={() => setMinutes(m)}
            className={`rounded-full border px-3 py-1.5 text-xs ${minutes === m ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground"}`}>
            {t("programs.quick.minutes", { n: m })}
          </button>
        ))}
      </div>
      <Button size="sm" className="rounded-2xl" disabled={busy} onClick={go}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("programs.quick.go")}
      </Button>
      {text && <AssistantMessage content={text} className="rounded-2xl bg-muted/50 p-3 text-sm" />}
    </Card>
  );
}

/* ------------------------------- THEORY ---------------------------------- */

function TheorySection({ topicId, initial }: { topicId: string; initial: string | null }) {
  const { t } = useTranslation();
  const run = useServerFn(generateTheory);
  const [body, setBody] = useState(initial ?? "");
  const [busy, setBusy] = useState(false);

  async function gen(mode: "standard" | "simplify" | "deepen" | "different" | "example") {
    setBusy(true);
    try {
      const res = await run({ data: { topicId, mode } });
      setBody(res.body);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  function read() {
    if (!body) return;
    try {
      const u = new SpeechSynthesisUtterance(body.replace(/[#*`$]/g, ""));
      u.lang = document.documentElement.lang || "it";
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(u);
    } catch { /* unsupported */ }
  }

  return (
    <Card className="card-soft space-y-3 rounded-3xl border-0 p-5">
      <div className="flex items-center gap-2 text-sm font-semibold">
        <BookOpen className="h-4 w-4 text-primary" /> {t("programs.topicPage.appTheory")}
      </div>
      {!body && (
        <Button className="rounded-2xl" disabled={busy} onClick={() => gen("standard")}>
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
          {t("programs.topicPage.generate")}
        </Button>
      )}
      {body && (
        <>
          <AssistantMessage content={body} className="text-sm leading-relaxed" />
          <div className="flex flex-wrap gap-2 pt-1">
            {([["simplify", "simplify"], ["deepen", "deepen"], ["different", "different"], ["example", "example"]] as const).map(([mode, k]) => (
              <Button key={mode} size="sm" variant="outline" className="rounded-full text-xs" disabled={busy} onClick={() => gen(mode)}>
                {t(`programs.topicPage.${k}`)}
              </Button>
            ))}
            <Button size="sm" variant="outline" className="rounded-full text-xs" onClick={read}>
              {t("programs.topicPage.read")}
            </Button>
          </div>
        </>
      )}
    </Card>
  );
}

/* ------------------------------ MATERIALS -------------------------------- */

function MaterialsSection({
  topicId, materials, onChanged,
}: {
  topicId: string;
  materials: { id: string; title: string; content: string | null }[];
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [attached, setAttached] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function save() {
    if (!content.trim()) return;
    setBusy(true);
    try {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) return;
      const { error } = await supabase.from("topic_materials").insert({
        topic_id: topicId,
        user_id: u.user.id,
        title: title.trim() || (attached ?? "Materiale"),
        kind: attached ? "upload" : "text",
        content: content.trim().slice(0, 40000),
      });
      if (error) throw new Error(error.message);
      setTitle(""); setContent(""); setAttached(null);
      onChanged();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    await supabase.from("topic_materials").delete().eq("id", id);
    onChanged();
  }

  return (
    <Card className="card-soft space-y-3 rounded-3xl border-0 p-5">
      <div className="flex items-center gap-2 text-sm font-semibold">
        <FolderOpen className="h-4 w-4 text-primary" /> {t("programs.topicPage.myTheory")}
      </div>
      <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t("programs.topicPage.materialTitle")} />
      <Textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        placeholder={t("programs.topicPage.materialPlaceholder")}
        className="min-h-28 rounded-2xl"
      />
      <MediaAttachButton
        onText={(txt, name) => { setContent((p) => (p ? `${p}\n\n${txt}` : txt)); setAttached(name); }}
        attachedName={attached}
        onClear={() => setAttached(null)}
      />
      <Button size="sm" className="rounded-2xl" disabled={busy} onClick={save}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="mr-1 h-3.5 w-3.5" /> {t("programs.topicPage.addMaterial")}</>}
      </Button>

      <div className="space-y-2 pt-2">
        {materials.map((m) => (
          <div key={m.id} className="flex items-start gap-2 rounded-2xl bg-muted/40 p-3">
            <div className="min-w-0 flex-1">
              <div className="text-xs font-medium">{m.title}</div>
              <p className="line-clamp-3 text-xs text-muted-foreground">{m.content}</p>
            </div>
            <button type="button" onClick={() => remove(m.id)} className="rounded-lg p-1 text-muted-foreground hover:text-destructive">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
        {!materials.length && <p className="text-xs text-muted-foreground">{t("programs.topicPage.noMaterials")}</p>}
      </div>
    </Card>
  );
}
